import React, { useEffect, useState } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../helpers/api';
import PropertyCardWithCarousel from './PropertyCardWithCarousel';

interface Property {
  id: string;
  mlsNumber: string;
  price: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  images?: string[];
  thumbnails?: string[];
  daysOnMarket?: number;
  propertyType?: string;
  matchScore?: number;
}

interface FeaturedPropertiesProps {
  userPreferences?: any;
}

const FeaturedProperties: React.FC<FeaturedPropertiesProps> = ({ userPreferences }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [savedProperties, setSavedProperties] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    loadFeaturedProperties();
  }, []);

  const loadFeaturedProperties = async () => {
    try {
      // Load trending properties from the new endpoint
      const response = await axiosInstance.get('/mls-portal/properties/trending?limit=12');
      
      if (response.data.success && response.data.data) {
        // Properties are already sorted by popularity/trending status
        const transformedProperties = response.data.data
          .filter((prop: any) => 
            prop.images?.length > 0 && 
            prop.price > 0 &&
            prop.bedrooms > 0
          )
          .slice(0, 4) // Take top 4 properties
          .map((prop: any, index: number) => ({
            ...prop,
            id: prop.mlsNumber || prop.id,
            // Use the viewCount from the trending API or calculate a match score
            matchScore: prop.viewCount || calculateMatchScore(prop, index)
          }));
        
        setProperties(transformedProperties);
      }
    } catch (error) {
      console.error('Failed to load featured properties:', error);
      // Fallback to some default properties if API fails
      setProperties(getFallbackProperties());
    }
  };

  const calculateMatchScore = (property: any, index: number): number => {
    // AI-powered scoring algorithm based on multiple factors
    let score = 80; // Base score for all properties
    
    // Location Score (up to +10 points)
    const premiumCities = ['Scottsdale', 'Paradise Valley'];
    const popularCities = ['Phoenix', 'Tempe', 'Mesa', 'Chandler', 'Gilbert'];
    if (premiumCities.includes(property.address?.city)) score += 10;
    else if (popularCities.includes(property.address?.city)) score += 7;
    else score += 3;
    
    // Price Value Score (up to +8 points)
    // Properties in the $400k-$500k range are most popular
    if (property.price >= 400000 && property.price <= 500000) score += 8;
    else if (property.price >= 350000 && property.price <= 600000) score += 5;
    else if (property.price < 350000) score += 6; // Good value properties
    
    // Property Size Score (up to +5 points)
    if (property.bedrooms >= 3 && property.bedrooms <= 4) score += 5;
    else if (property.bedrooms >= 5) score += 3;
    else if (property.bedrooms === 2) score += 2;
    
    // Market Freshness Score (up to +7 points)
    if (property.daysOnMarket !== undefined) {
      if (property.daysOnMarket <= 7) score += 7;  // Brand new listing
      else if (property.daysOnMarket <= 14) score += 5;
      else if (property.daysOnMarket <= 30) score += 3;
    }
    
    // Visual Quality Score (up to +5 points)
    const imageCount = (property.images?.length || 0) + (property.thumbnails?.length || 0);
    if (imageCount >= 20) score += 5;
    else if (imageCount >= 10) score += 4;
    else if (imageCount >= 5) score += 2;
    
    // Ensure scores are between 70-99 and add slight variation
    score = Math.max(70, Math.min(99, score - (index * 1)));
    
    return score;
  };

  const getFallbackProperties = (): Property[] => {
    // Fallback properties if API fails
    return [
      {
        id: 'FB001',
        mlsNumber: 'FB001',
        price: 425000,
        address: {
          street: '123 Desert View Dr',
          city: 'Scottsdale',
          state: 'AZ',
          zip: '85251'
        },
        bedrooms: 3,
        bathrooms: 2,
        sqft: 2150,
        images: ['https://via.placeholder.com/400x300?text=Beautiful+Home'],
        matchScore: 95
      },
      {
        id: 'FB002',
        mlsNumber: 'FB002',
        price: 389900,
        address: {
          street: '456 Sunny Lane',
          city: 'Phoenix',
          state: 'AZ',
          zip: '85018'
        },
        bedrooms: 3,
        bathrooms: 2.5,
        sqft: 1980,
        images: ['https://via.placeholder.com/400x300?text=Modern+Living'],
        matchScore: 92
      },
      {
        id: 'FB003',
        mlsNumber: 'FB003',
        price: 445000,
        address: {
          street: '789 Mountain View',
          city: 'Tempe',
          state: 'AZ',
          zip: '85281'
        },
        bedrooms: 4,
        bathrooms: 3,
        sqft: 2450,
        images: ['https://via.placeholder.com/400x300?text=Family+Home'],
        matchScore: 89
      },
      {
        id: 'FB004',
        mlsNumber: 'FB004',
        price: 510000,
        address: {
          street: '321 Cactus Trail',
          city: 'Mesa',
          state: 'AZ',
          zip: '85204'
        },
        bedrooms: 4,
        bathrooms: 3,
        sqft: 2800,
        images: ['https://via.placeholder.com/400x300?text=Luxury+Estate'],
        matchScore: 86
      }
    ];
  };

  const handleSaveProperty = (propertyId: string) => {
    setSavedProperties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  const handleViewProperty = (propertyId: string) => {
    // Navigate to customer portal with this specific property
    localStorage.setItem('chatRealEstateProperty', propertyId);
    navigate('/search', { state: { propertyId } });
  };

  return (
    <div className="featured-properties-section">
      <h2 className="section-title">
        <span style={{ opacity: 1, display: 'inline-block' }}>🏠</span> Trending Properties
      </h2>
      <p className="section-subtitle">
        Most popular homes based on recent searches and market activity
      </p>
      
      <Row>
        {properties.map((property) => (
          <Col key={property.id} lg={3} md={6} className="mb-4">
            <PropertyCardWithCarousel
              property={property}
              onSave={handleSaveProperty}
              onView={handleViewProperty}
              isSaved={savedProperties.has(property.id)}
            />
          </Col>
        ))}
      </Row>
      
      <div className="text-center mt-5">
        <Button 
          variant="outline-primary" 
          size="lg"
          onClick={() => navigate('/search')}
        >
          View All Properties
        </Button>
      </div>
    </div>
  );
};

export default FeaturedProperties;